import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { NAV_ITEMS } from './nav';
import { useThemeMode } from '../../hooks/useThemeMode';
import riskService from '../../services/risk.service';
import { Risk } from '../../types/risk.types';
import { cn } from '../../lib/cn';

const SHORTCUTS: Array<[string, string]> = [
  ['⌘K / Ctrl+K', 'Open command palette'],
  ['g then d / r / t / p / c / o / s', 'Go to section'],
  ['c then r / t', 'Record risk / Add task'],
  ['?', 'This sheet'],
  ['Esc', 'Close'],
];

export const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Risk[]>([]);
  const pendingKey = useRef<string | null>(null);
  const navigate = useNavigate();
  const { setMode } = useThemeMode();

  const go = useCallback(
    (path: string) => {
      setOpen(false);
      setQuery('');
      navigate(path);
    },
    [navigate]
  );

  // global keys: ⌘K, ?, and g/c sequences
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' ||
        (e.target as HTMLElement)?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (typing || open) return;
      if (e.key === '?') { setSheetOpen(true); return; }
      if (pendingKey.current === 'g') {
        pendingKey.current = null;
        const item = NAV_ITEMS.find((n) => n.key === e.key);
        if (item) navigate(item.path);
        return;
      }
      if (pendingKey.current === 'c') {
        pendingKey.current = null;
        if (e.key === 'r') navigate('/risks?create=1');
        if (e.key === 't') navigate('/tasks?create=1');
        return;
      }
      if (e.key === 'g' || e.key === 'c') {
        pendingKey.current = e.key;
        setTimeout(() => { pendingKey.current = null; }, 1200);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, navigate]);

  // live risk search
  useEffect(() => {
    if (!open || query.trim().length < 2) { setHits([]); return; }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const risks = await riskService.getRisks({ search: query.trim() });
        if (live) setHits((risks || []).slice(0, 5));
      } catch { if (live) setHits([]); }
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [query, open]);

  return (
    <>
      <Command.Dialog
        open={open}
        onOpenChange={setOpen}
        label="Command palette"
        className={cn(
          'fixed left-1/2 top-24 z-[1400] w-full max-w-lg -translate-x-1/2',
          'border border-ink bg-paper-raised text-ink shadow-none'
        )}
        overlayClassName="fixed inset-0 z-[1399] bg-ink/30"
      >
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder="Search or jump to…"
          className="w-full border-b-2 border-ink bg-transparent px-4 py-3 font-sans text-scale-2 outline-none placeholder:italic placeholder:font-serif placeholder:text-ink-muted"
        />
        <Command.List className="max-h-80 overflow-y-auto p-2">
          <Command.Empty className="px-3 py-4 font-serif italic text-ink-muted">
            Nothing in the register matches.
          </Command.Empty>
          {hits.length > 0 && (
            <Command.Group heading="Risks" className="cap-label px-2 pt-2">
              {hits.map((r) => (
                <Command.Item key={r.id} value={`risk-${r.id}`}
                  onSelect={() => go('/risks')}
                  className="flex cursor-pointer items-baseline gap-2 px-2 py-1.5 font-sans text-scale-2 data-[selected=true]:bg-accent-soft data-[selected=true]:text-accent">
                  <span className="font-mono text-scale-0 text-ink-muted">{r.reference_number}</span>
                  {r.title}
                </Command.Item>
              ))}
            </Command.Group>
          )}
          <Command.Group heading="Navigate" className="cap-label px-2 pt-2">
            {NAV_ITEMS.map((n) => (
              <Command.Item key={n.path} value={n.label} onSelect={() => go(n.path)}
                className="flex cursor-pointer items-center justify-between px-2 py-1.5 font-sans text-scale-2 data-[selected=true]:bg-accent-soft data-[selected=true]:text-accent">
                {n.label}
                <span className="font-mono text-scale-0 text-ink-muted">g {n.key}</span>
              </Command.Item>
            ))}
          </Command.Group>
          <Command.Group heading="Create" className="cap-label px-2 pt-2">
            <Command.Item value="Record risk" onSelect={() => go('/risks?create=1')}
              className="cursor-pointer px-2 py-1.5 font-sans text-scale-2 data-[selected=true]:bg-accent-soft data-[selected=true]:text-accent">
              Record risk
            </Command.Item>
            <Command.Item value="Add task" onSelect={() => go('/tasks?create=1')}
              className="cursor-pointer px-2 py-1.5 font-sans text-scale-2 data-[selected=true]:bg-accent-soft data-[selected=true]:text-accent">
              Add task
            </Command.Item>
          </Command.Group>
          <Command.Group heading="Theme" className="cap-label px-2 pt-2">
            {(['light', 'dark', 'system'] as const).map((m) => (
              <Command.Item key={m} value={`theme ${m}`}
                onSelect={() => { setMode(m); setOpen(false); }}
                className="cursor-pointer px-2 py-1.5 font-sans text-scale-2 capitalize data-[selected=true]:bg-accent-soft data-[selected=true]:text-accent">
                {m}
              </Command.Item>
            ))}
          </Command.Group>
        </Command.List>
      </Command.Dialog>

      {sheetOpen && (
        <div className="fixed inset-0 z-[1400] flex items-start justify-center bg-ink/30 pt-24"
          onClick={() => setSheetOpen(false)}>
          <div className="w-full max-w-md border border-ink bg-paper-raised p-5 text-ink"
            onClick={(e) => e.stopPropagation()}>
            <div className="rule-double pb-2 font-serif text-scale-4">Keyboard shortcuts</div>
            <dl className="mt-3 space-y-2">
              {SHORTCUTS.map(([k, d]) => (
                <div key={k} className="flex items-baseline justify-between gap-4">
                  <dt className="font-mono text-scale-1">{k}</dt>
                  <dd className="font-sans text-scale-1 text-ink-muted">{d}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </>
  );
};
